import { WidgetStrategyWithStreamByRequestUid } from 'widgets/WidgetRegistry';
import { WidgetCategory } from 'widgets/WidgetCategory';
import { PortfolioSelectionRequirements } from 'widgets/PortfolioSelectionRequirements';
import { SignalTileConfigState } from 'widgets/signalTiles/signalTile/state/config/SignalTileConfigState';
import { WidgetComponentProps } from 'widgets/WidgetComponentProps';
import { DropdownTooltipText } from '@instinet-analytics/semantic-ui-components';
import { buildStreamConfig } from 'widgets/signalTiles/signalTile/state/stream/stream-config';
import { WidgetType } from 'widgets/WidgetType';
import { ChannelDestination } from 'communication/Channel';
import { ConfigSlice } from 'widgets/store/configuration/configSlice';
import { SignalTileStreamRequest } from 'widgets/signalTiles/signalTile/api/SignalTileStreamRequest';
import {
    MyPortfolioCapsuleContentProps,
    MyPortfolioCapsuleWidgetStrategyWithConfig,
} from 'widgets/myPortfolioCapsules/MyPortfolioCapsuleWidgetStrategy';
import { ObvTileConfigState } from 'widgets/obvTile/store/config/ObvTileConfigState';
import React from 'react';
import SignalTileSettings from 'widgets/signalTiles/signalTile/components/settings/SignalTileSettings';
import { StreamMessage } from 'utils/messages/StreamMessage';
import { useSignalCapsuleMessagesInMyPortfolioCapsules } from 'widgets/signalTiles/signalTile/useSignalCapsuleMessagesInMyPortfolioCapsules';

export interface SignalTileWidgetStrategyOptions {
    title: string;
    reactComponentType: React.FC<WidgetComponentProps>;
    description: DropdownTooltipText;
    widgetType: WidgetType;
    startChannelDestination: ChannelDestination;
    configSlice: ConfigSlice<SignalTileConfigState>;
    myPortfolioCapsuleContentReactComponentType: React.FC<MyPortfolioCapsuleContentProps>;
    useStreamedDataMessages: (
        symbol: string,
        config: SignalTileConfigState
    ) => StreamMessage[];
}

export function buildSignalTileWidgetStrategy(
    options: SignalTileWidgetStrategyOptions
): MyPortfolioCapsuleWidgetStrategyWithConfig<SignalTileConfigState> &
    WidgetStrategyWithStreamByRequestUid<SignalTileStreamRequest> {
    const {
        title,
        reactComponentType,
        description,
        configSlice,
        myPortfolioCapsuleContentReactComponentType,
        useStreamedDataMessages,
    } = options;

    const useMessagesInMyPortfolioCapsules = (symbol: string) =>
        useSignalCapsuleMessagesInMyPortfolioCapsules(
            symbol,
            useStreamedDataMessages
        );

    const managedStreamConfig = buildStreamConfig(options);
    const {
        hooks: { useDispatchUpdateConfigAction: dispatchConfigChanged },
    } = configSlice;

    return {
        title: title,
        supportedHosts: ['tile'],
        reactComponentType,
        categories: [WidgetCategory.SIGNAL_CAPSULES],
        description,
        portfolioSelectionRequirements:
            PortfolioSelectionRequirements.SingleSymbolQuantity,
        initialConfigState: configSlice.initialConfigState,
        managedStreamConfig,
        supportsMyPortfolioCapsule: true,
        supportsMyPortfolioCapsuleConfig: true,
        myPortfolioCapsuleContentReactComponentType,
        renderSettingsBody,
        useMessagesInMyPortfolioCapsules,
    };

    function renderSettingsBody(
        config: SignalTileConfigState,
        onChange: (configDelta: Partial<ObvTileConfigState>) => void
    ) {
        return (
            <SignalTileSettings
                config={config}
                onChange={onChange}
                dispatchConfigChanged={dispatchConfigChanged}
            />
        );
    }
}
